import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, X, Search, Check } from 'lucide-react';
import { Product, ProductDomain } from '../../types/product';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

interface ProductSelectorProps {
  domains: ProductDomain[];
  selectedProductName?: string | null;  // Deprecated - use selectedProductCode
  selectedProductCode: string | null;   // The reporting code (e.g., "9002")
  onChange: (productName: string | null, productCode: string | null, productTitle: string | null) => void;
  disabled?: boolean;
}

export const ProductSelector: React.FC<ProductSelectorProps> = ({
  domains,
  selectedProductCode,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Find the selected product details by code (reporting code)
  const selectedProduct = useMemo(() => {
    if (!selectedProductCode) return null;
    for (const domain of domains) {
      const product = domain.products.find(p => p.code === selectedProductCode);
      if (product) return { product, domainName: domain.name };
    }
    return null;
  }, [domains, selectedProductCode]);

  // Filter domains and products based on search
  const filteredDomains = useMemo(() => {
    if (!searchTerm) return domains;

    const lowerSearch = searchTerm.toLowerCase();
    return domains
      .map(domain => ({
        ...domain,
        products: domain.products.filter(
          p =>
            p.title.toLowerCase().includes(lowerSearch) ||
            p.name.toLowerCase().includes(lowerSearch) ||
            (p.code && p.code.toLowerCase().includes(lowerSearch))
        ),
      }))
      .filter(domain => domain.products.length > 0);
  }, [domains, searchTerm]);

  // Auto-expand domain of selected product when opening
  useEffect(() => {
    if (isOpen && selectedProduct) {
      setExpandedDomains(prev => new Set([...prev, selectedProduct.domainName]));
    }
  }, [isOpen, selectedProduct]);

  // Focus search input when sheet opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset search when closing
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const toggleDomain = (domainName: string) => {
    setExpandedDomains(prev => {
      const newSet = new Set(prev);
      if (newSet.has(domainName)) {
        newSet.delete(domainName);
      } else {
        newSet.add(domainName);
      }
      return newSet;
    });
  };

  const handleSelect = (product: Product) => {
    onChange(product.name, product.code, product.title);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null, null, null);
  };

  return (
    <>
      {/* Trigger Button */}
      <div className="relative">
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-start font-normal h-9",
            !selectedProduct && "text-muted-foreground"
          )}
          onClick={() => setIsOpen(true)}
          disabled={disabled}
        >
          {selectedProduct ? (
            <span className="truncate">
              {selectedProduct.product.title}
              {selectedProduct.product.code && (
                <span className="text-muted-foreground ml-2 font-mono text-xs">
                  ({selectedProduct.product.code})
                </span>
              )}
            </span>
          ) : (
            "Select a product..."
          )}
        </Button>
        {selectedProduct && !disabled && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded z-10"
            onClick={handleClear}
            aria-label="Clear selection"
          >
            <X className="h-3 w-3 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Product Picker Sheet */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[400px] p-0 flex flex-col">
          <SheetHeader className="p-4 pb-0">
            <SheetTitle>Select Product</SheetTitle>
          </SheetHeader>

          {/* Search Input - Sticky */}
          <div className="px-4 py-3 border-b sticky top-0 bg-background">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Product List - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            {filteredDomains.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No products found
              </div>
            ) : (
              filteredDomains.map(domain => {
                const isExpanded = expandedDomains.has(domain.name) || !!searchTerm;
                return (
                  <div key={domain.name} className="border-b last:border-b-0">
                    {/* Domain Header */}
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-4 py-3 text-sm font-medium hover:bg-muted text-left"
                      onClick={() => toggleDomain(domain.name)}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <span className="flex-1">{domain.name}</span>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                        {domain.products.length}
                      </span>
                    </button>

                    {/* Products List */}
                    {isExpanded && (
                      <div className="pb-1">
                        {domain.products.map((product, index) => {
                          const isSelected = selectedProductCode === product.code;
                          return (
                            <button
                              key={`${domain.name}-${product.name}-${index}`}
                              type="button"
                              data-testid="product-item"
                              className={cn(
                                "flex w-full items-center gap-2 pl-10 pr-4 py-2 text-sm text-left hover:bg-muted",
                                isSelected && "bg-accent"
                              )}
                              onClick={() => handleSelect(product)}
                            >
                              <Check
                                className={cn(
                                  "h-4 w-4 shrink-0",
                                  isSelected ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <span className="flex-1 truncate">{product.title}</span>
                              {product.code && (
                                <span className="text-xs text-muted-foreground font-mono">
                                  {product.code}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
